import React, { useState, useEffect } from 'react';
import { mfaApi } from '../api';
import { Btn, Alert } from '../components/ui';

export default function MfaSetup({ onComplete, onCancel }) {
  const [step,    setStep]    = useState('start'); // start | scan | confirm | done
  const [data,    setData]    = useState(null);
  const [code,    setCode]    = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoad]    = useState(false);

  const startSetup = async () => {
    setLoad(true); setError('');
    try {
      const res = await mfaApi.setup();
      setData(res.data?.data);
      setStep('scan');
    } catch (e) { setError(e.response?.data?.message || 'Failed to start setup'); }
    finally { setLoad(false); }
  };

  const confirmSetup = async () => {
    if (!code.trim() || code.trim().length !== 6) { setError('Enter the 6-digit code'); return; }
    setLoad(true); setError('');
    try {
      await mfaApi.confirmSetup(parseInt(code.trim()));
      setStep('done');
    } catch (e) { setError(e.response?.data?.message || 'Invalid code'); }
    finally { setLoad(false); }
  };

  if (step === 'start') return (
    <div style={S.wrap}>
      <div style={S.iconWrap}>🔐</div>
      <h2 style={S.title}>Enable Two-Factor Authentication</h2>
      <p style={S.sub}>
        After enabling, every login will require a 6-digit code from your authenticator app.
        This protects your account even if your password is compromised.
      </p>
      <div style={S.steps}>
        <div style={S.step}><span style={S.stepN}>1</span>Open Google Authenticator or Microsoft Authenticator on your company phone</div>
        <div style={S.step}><span style={S.stepN}>2</span>Scan the QR code we'll show you</div>
        <div style={S.step}><span style={S.stepN}>3</span>Enter the 6-digit code to confirm setup</div>
      </div>
      {error && <Alert type="error" message={error} />}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <Btn onClick={startSetup} loading={loading} fullWidth>Start Setup →</Btn>
        {onCancel && <Btn variant="ghost" onClick={onCancel} fullWidth>Cancel</Btn>}
      </div>
    </div>
  );

  if (step === 'scan') return (
    <div style={S.wrap}>
      <h2 style={S.title}>Scan QR Code</h2>
      <p style={S.sub}>
        Open your authenticator app, tap <strong>Add account</strong>, then scan this QR code.
      </p>

      {/* QR Code rendered via Google Charts API */}
      <div style={S.qrWrap}>
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data?.qrUrl || '')}`}
          alt="QR Code for authenticator app"
          style={{ width: '200px', height: '200px', borderRadius: '12px' }}
        />
      </div>

      <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '6px', textAlign: 'center' }}>
        Can't scan? Enter this code manually:
      </p>
      <div style={S.secretBox}>
        <code style={{ fontSize: '15px', letterSpacing: '.1em', fontWeight: 700, color: 'var(--color-primary)' }}>
          {data?.secret?.match(/.{1,4}/g)?.join(' ')}
        </code>
      </div>

      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: '16px 0 8px', textAlign: 'center' }}>
        After scanning, enter the 6-digit code from the app:
      </p>

      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
        style={S.codeInput}
        autoFocus
      />

      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <Btn onClick={confirmSetup} loading={loading} fullWidth>Confirm &amp; Enable MFA →</Btn>
      </div>
    </div>
  );

  if (step === 'done') return (
    <div style={{ ...S.wrap, textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
      <h2 style={S.title}>MFA Enabled</h2>
      <p style={S.sub}>
        Your account is now protected with two-factor authentication.
        Every login will require your authenticator app.
      </p>
      <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
        <p style={{ fontSize: '13px', color: '#92400E', fontWeight: 600 }}>⚠ Important</p>
        <p style={{ fontSize: '13px', color: '#78350F', marginTop: '4px', lineHeight: 1.65 }}>
          If you lose your company phone, contact your Super Admin or Mahashunya support to reset MFA.
          Without the phone, you will not be able to login.
        </p>
      </div>
      <Btn onClick={onComplete} fullWidth>Done</Btn>
    </div>
  );

  return null;
}

const S = {
  wrap: { maxWidth: '460px', margin: '0 auto', padding: '8px' },
  iconWrap: { fontSize: '40px', marginBottom: '14px', textAlign: 'center' },
  title: { fontSize: '20px', fontWeight: 800, color: 'var(--color-text)', marginBottom: '10px', letterSpacing: '-0.02em' },
  sub: { fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.68, marginBottom: '20px' },
  steps: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' },
  step: { display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: 'var(--color-text)', lineHeight: 1.55 },
  stepN: {
    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
    background: 'var(--color-primary)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: 800, marginTop: '1px',
  },
  qrWrap: { display: 'flex', justifyContent: 'center', margin: '20px 0 16px', padding: '12px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid var(--color-border)' },
  secretBox: { background: '#F8FAFC', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '12px', textAlign: 'center', marginBottom: '4px' },
  codeInput: {
    width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid var(--color-border)',
    fontSize: '22px', fontWeight: 800, textAlign: 'center', letterSpacing: '.2em',
    outline: 'none', fontFamily: 'monospace',
  },
};
